import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import ActivityIcon from "src/assets/all-icons/activity-icon.jsx";
import ArtworkIcon from "src/assets/all-icons/artwork-icon.jsx";
import ChatbotIcon from "src/assets/all-icons/chatbot-icon.jsx";
import CollectionIcon from "src/assets/all-icons/collection-icon.jsx";
import CreditCardIcon from "src/assets/all-icons/credit-card-icon.jsx";
import EditArtworkIcon from "src/assets/all-icons/edit-artwork-icon.jsx";
import HelpCenterIcon from "src/assets/all-icons/help-center-icon.jsx";
import LimitedEditionIcon from "src/assets/all-icons/limited-edition-icon.jsx";
import OrdersIcon from "src/assets/all-icons/orders-icon.jsx";
import SettingsIcon from "src/assets/all-icons/settings-icon.jsx";
import SuarteRoadIcon from "src/assets/all-icons/suarte-road-icon.jsx";

import ArtistIcon from '@/assets/all-icons/artist-icon';
import SaveArtworkIcon from '@/assets/all-icons/save-artwork-icon';
import UserIcon from '@/assets/all-icons/user-icon';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import AuthService from '@/services/auth.service';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import Text from '@/shared-components/text/components/Text';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import './styles/ProfileOptions.css';

export default function ProfileOptions({ fetchData, open, setOpen }) {
  const { state } = useStateHandler();
  const t = useTranslations('common.profile.options');
  const session = state.user_session;
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  const [requiredActionMessage, setRequiredActionMessage] = useState('');

  const canAccessInventory = canAccessGalleryPermission(
    session,
    "artworks.inventory",
    fetchData?._id
  );
  const normalizedProfileType = String(fetchData?.user_type || fetchData?.__t || '').toLowerCase();
  const isGalleryMember = session?.user_type !== "gallery" && Boolean(session?.gallery_id);
  const editProfileOption = (
    <Link className="profile-options__option" to="/profile/settings/edit-profile" state={{ from: true }}>
      <Text className="profile-options__option-text delay1 font-size-small">
        {t('options.edit_profile')}
      </Text>

      <div className="profile-options__option-icon-container delay1">
        <EditArtworkIcon className="profile-options__option-icon"/>
      </div>
    </Link>
  );

  function handleBlockedOptionClick(event, optionLabel, message) {
    if (!isGalleryMember) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setRequiredActionMessage(message || t('errors.gallery_member_blocked', { optionLabel }));
    setShowActionRejectedPopup(true);
  }

  const requiredActionPopup = (
    <ActionRejectedPopup
      open={showActionRejectedPopup}
      close={() => setShowActionRejectedPopup(false)}
      description={requiredActionMessage}
    />
  );

  useEffect(()=>{
    if(open === true) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }, [open]);

  useEffect(()=>{
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, []);

  if(!open) {
    return <></>;
  } else if(normalizedProfileType === "artist" || normalizedProfileType === "collector") {
    return createPortal(
      <>
        <div className="profile-options__background" onClick={()=>setOpen(false)}>
          <div className="profile-options__options-outter">
            <div className="profile-options__options">
              {editProfileOption}

              <Link className="profile-options__option" to="suarte-road" state={{ from: true }}>
                <Text className="profile-options__option-text delay1 font-size-small">
                  {t('options.suarte_road')}
                </Text>

                <div className="profile-options__option-icon-container delay1">
                  <SuarteRoadIcon className="profile-options__option-icon"/>
                </div>
              </Link>

              <Link className="profile-options__option" to="my-collection" state={{ from: true }}>
                <Text className="profile-options__option-text delay1 font-size-small">
                  {t('options.my_collection')}
                </Text>

                <div className="profile-options__option-icon-container delay1">
                  <CollectionIcon className="profile-options__option-icon"/>
                </div>
              </Link>

              <Link className="profile-options__option" to="/orders/confirmed" state={{ from: true }}>
                <Text className="profile-options__option-text delay2 font-size-small">
                  {t('options.orders')}
                </Text>

                <div className="profile-options__option-icon-container delay2">
                  <OrdersIcon className="profile-options__option-icon"/>
                </div>
              </Link>
              {normalizedProfileType === "collector" && (
                <Link className="profile-options__option" to="/profile/offers" state={{ from: true }}>
                  <Text className="profile-options__option-text delay3 font-size-small">
                    {t('options.offers')}
                  </Text>

                  <div className="profile-options__option-icon-container delay3">
                    <LimitedEditionIcon className="profile-options__option-icon"/>
                  </div>
                </Link>
              )}

              <Link
                className="profile-options__option"
                to="activity"
                state={{ from: true }}
                onClick={(event) => handleBlockedOptionClick(event, t('options.activity'))}
              >
                <Text className="profile-options__option-text delay3 font-size-small">
                  {t('options.activity')}
                </Text>

                <div className="profile-options__option-icon-container delay3">
                  <ActivityIcon className="profile-options__option-icon"/>
                </div>
              </Link>

              <Link
                className="profile-options__option"
                to="activity/saved-artworks"
                state={{ from: true }}
                onClick={(event) => handleBlockedOptionClick(event, t('options.saved_artworks'))}
              >
                <Text className="profile-options__option-text delay4 font-size-small">
                  {t('options.saved_artworks')}
                </Text>

                <div className="profile-options__option-icon-container delay4">
                  <SaveArtworkIcon className="profile-options__option-icon"/>
                </div>
              </Link>

              <Link
                className="profile-options__option"
                to="/payments-profile"
                state={{ from: true }}
                onClick={(event) => handleBlockedOptionClick(event, t('options.payments'))}
              >
                <Text className="profile-options__option-text delay8 font-size-small">
                  {t('options.payments')}
                </Text>

                <div className="profile-options__option-icon-container delay8">
                  <CreditCardIcon className="profile-options__option-icon"/>
                </div>
              </Link>

              {normalizedProfileType === "collector" && (
                <Link className="profile-options__option" to="/profile/collector-help" state={{ from: true }}>
                  <Text className="profile-options__option-text delay8 font-size-small">
                    {t('options.collector_help')}
                  </Text>

                  <div className="profile-options__option-icon-container delay8">
                    <HelpCenterIcon className="profile-options__option-icon stroke-0"/>
                  </div>
                </Link>
              )}

              {normalizedProfileType === "artist" && (
                <>
                  <Link className="profile-options__option" to="/profile/ai-setup" state={{ from: true }}>
                    <Text className="profile-options__option-text delay8 font-size-small">
                      {t('options.ai_setup')}
                    </Text>

                    <div className="profile-options__option-icon-container delay8">
                      <ChatbotIcon className="sea-widget__launcher-icon profile-options__ai-setup-icon" aria-hidden />
                    </div>
                  </Link>

                  <Link className="profile-options__option" to="/profile/artist-help" state={{ from: true }}>
                    <Text className="profile-options__option-text delay8 font-size-small">
                      {t('options.artist_help')}
                    </Text>

                    <div className="profile-options__option-icon-container delay8">
                      <HelpCenterIcon className="profile-options__option-icon stroke-0"/>
                    </div>
                  </Link>
                </>
              )}

              {!isGalleryMember && (
                <Link className="profile-options__option" to="settings" state={{ from: true }}>
                  <Text className="profile-options__option-text delay5 font-size-small">
                    {t('options.settings')}
                  </Text>

                  <div className="profile-options__option-icon-container delay5">
                    <SettingsIcon className="profile-options__option-icon"/>
                  </div>
                </Link>
              )}

              {isGalleryMember && (
                <div
                  className="profile-options__option"
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    AuthService.logout();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.stopPropagation();
                      AuthService.logout();
                    }
                  }}
                >
                  <Text className="profile-options__option-text delay5 font-size-small">
                    {t('options.log_out')}
                  </Text>

                  <div className="profile-options__option-icon-container delay5">
                    <SettingsIcon className="profile-options__option-icon"/>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {requiredActionPopup}
      </>, document.getElementById("portal-profile-options")
    );
  } else {
    return createPortal(
      <>
        <div className="profile-options__background" onClick={()=>setOpen(false)}>
          <div className="profile-options__options-outter">
            <div className="profile-options__options">
              {editProfileOption}

              {/* Suarte Road temporarily disabled for gallery profiles */}
              <Link
                className="profile-options__option"
                to="/inventory/available"
                state={{ from: true }}
                onClick={(event) => {
                  event.stopPropagation();
                  if (canAccessInventory || !isGalleryMember) {
                    return;
                  }
                  handleBlockedOptionClick(
                    event,
                    t('options.inventory'),
                    t('errors.inventory_permission_required')
                  );
                }}
              >
                <Text className="profile-options__option-text delay1 font-size-small">
                  {t('options.inventory')}
                </Text>

                <div className="profile-options__option-icon-container delay1">
                  <ArtworkIcon className="profile-options__option-icon"/>
                </div>
              </Link>

              <Link className="profile-options__option" to="/my-artists" state={{ from: true }}>
                <Text className="profile-options__option-text delay1 font-size-small">
                  {t('options.artists')}
                </Text>

                <div className="profile-options__option-icon-container delay1">
                  <ArtistIcon className="profile-options__option-icon"/>
                </div>
              </Link>
    
              <Link className="profile-options__option" to="/orders/confirmed" state={{ from: true }}>
                <Text className="profile-options__option-text delay2 font-size-small">
                  {t('options.orders')}
                </Text>

                <div className="profile-options__option-icon-container delay2">
                  <OrdersIcon className="profile-options__option-icon"/>
                </div>
              </Link>
    
              <Link
                className="profile-options__option"
                to="activity"
                state={{ from: true }}
                onClick={(event) => handleBlockedOptionClick(event, t('options.activity'))}
              >
                <Text className="profile-options__option-text delay3 font-size-small">
                  {t('options.activity')}
                </Text>

                <div className="profile-options__option-icon-container delay3">
                  <ActivityIcon className="profile-options__option-icon"/>
                </div>
              </Link>
    
              <Link
                className="profile-options__option"
                to="activity/saved-artworks"
                state={{ from: true }}
                onClick={(event) => handleBlockedOptionClick(event, t('options.saved_artworks'))}
              >
                <Text className="profile-options__option-text delay5 font-size-small">
                  {t('options.saved_artworks')}
                </Text>

                <div className="profile-options__option-icon-container delay5">
                  <SaveArtworkIcon className="profile-options__option-icon saved"/>
                </div>
              </Link>
    
              <Link className="profile-options__option" to="referrals" state={{ from: true }}>
                <Text className="profile-options__option-text delay6 font-size-small">
                  {t('options.referrals')}
                </Text>

                <div className="profile-options__option-icon-container delay6">
                  <UserIcon className="profile-options__option-icon referrals"/>
                </div>
              </Link>

              <Link
                className="profile-options__option"
                to="/payments-profile"
                state={{ from: true }}
                onClick={(event) => handleBlockedOptionClick(event, t('options.payments'))}
              >
                <Text className="profile-options__option-text delay8 font-size-small">
                  {t('options.payments')}
                </Text>

                <div className="profile-options__option-icon-container delay8">
                  <CreditCardIcon className="profile-options__option-icon"/>
                </div>
              </Link>

              {normalizedProfileType === "gallery" && (
                <Link className="profile-options__option" to="/profile/gallery-help" state={{ from: true }}>
                  <Text className="profile-options__option-text delay7 font-size-small">
                    Galleries Help
                  </Text>

                  <div className="profile-options__option-icon-container delay7">
                    <HelpCenterIcon className="profile-options__option-icon stroke-0"/>
                  </div>
                </Link>
              )}

              {!isGalleryMember && (
                <Link className="profile-options__option" to="settings" state={{ from: true }}>
                  <Text className="profile-options__option-text delay7 font-size-small">
                    {t('options.settings')}
                  </Text>
                                  
                  <div className="profile-options__option-icon-container delay7">
                    <SettingsIcon className="profile-options__option-icon"/>
                  </div>
                </Link>
              )}

              {isGalleryMember && (
                <div
                  className="profile-options__option"
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    AuthService.logout();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.stopPropagation();
                      AuthService.logout();
                    }
                  }}
                >
                  <Text className="profile-options__option-text delay7 font-size-small">
                    {t('options.log_out')}
                  </Text>

                  <div className="profile-options__option-icon-container delay7">
                    <SettingsIcon className="profile-options__option-icon"/>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
        {requiredActionPopup}
      </>, document.getElementById("portal-profile-options")
    );
  }
}
